import { APIStatus } from "@/api/APIStatus.type";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { useTranslation } from "@repo/shared/i18n";
import { SbcFodderCalculationEntry } from "@repo/shared/redux";
import { useMemo } from "react";

type SbcFodderBreakdownTableProps = {
  calculation: Record<string, SbcFodderCalculationEntry>;
  fetchClubStatsStatus?: APIStatus;
};

const formatNumber = (value: number) => value.toLocaleString("en-US");

/**
 * The club's SBC fodder by rating: how many players at each rating and the
 * Item Score they add up to. Laid out like My Players, edge to edge.
 */
export const SbcFodderBreakdownTable = ({
  calculation,
  fetchClubStatsStatus,
}: SbcFodderBreakdownTableProps) => {
  const { t } = useTranslation(["myClub", "common"]);
  const rows = useMemo(
    () =>
      Object.entries(calculation)
        .map(([rating, entry]) => ({ rating: Number(rating), ...entry }))
        .sort((a, b) => b.rating - a.rating),
    [calculation],
  );
  const loading =
    fetchClubStatsStatus === "pending" || fetchClubStatsStatus === "idle";

  return (
    <table className="w-full table-fixed text-left text-sm">
      <thead className="bg-neutral-800 text-xs font-semibold tracking-wider text-foreground-muted uppercase">
        <tr>
          <th className="w-1/3 px-4 py-3 mobile:px-6">{t("common:rating")}</th>
          <th className="w-1/3 px-2 py-3 mobile:px-3">
            {t("fodder.table.playersCount")}
          </th>
          <th className="px-2 py-3 mobile:px-3">
            {t("fodder.table.itemScore")}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={3}
              className="px-4 py-12 text-center text-foreground-muted"
            >
              {loading ? t("fodder.loading") : t("fodder.empty")}
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr
              key={row.rating}
              className="border-t border-neutral-800 bg-neutral-900 transition-colors hover:bg-neutral-800/70"
            >
              <td className="px-4 py-3 font-semibold text-neutral-100 tabular-nums mobile:px-6">
                {formatNumber(row.rating)}
              </td>
              <td className="px-2 py-3 text-neutral-300 tabular-nums mobile:px-3">
                {formatNumber(row.count)}
              </td>
              <td className="px-2 py-3 mobile:px-3">
                <span className="flex items-center gap-1.5 font-oswald text-base font-medium text-neutral-50 tabular-nums">
                  <ItemScoreDiamond size={14} />
                  <span>{formatNumber(row.itemScore)}</span>
                </span>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};
